import clsx from 'clsx';
import ConversionBar from 'components/Shared/ConversionBar';
import GetInTouch from 'components/Shared/GetInTouch';
import RecentStories from 'components/Shared/RecentStories';
import TrustBanner from 'components/Shared/TrustBanner';
import BlockLoaderNonFields from 'components/ui/ContentBlocks/BlockLoaderNonFields';
import RichText from 'components/ui/ContentBlocks/RichText';
import Icon from 'components/ui/Icon';
import { useTranslation } from 'hooks/useTranslation';
import BeforeAfter from './BeforeAfter';
import Clinics from './Clinics';
import Doctors from './Doctors';
import Hero from './Hero';
import type { LocationPage as LocationPageType } from 'types/PageData';
import type { IBlockGenericCtaFields } from 'types/contentful/generated';
import type { ClinicPage } from 'types/Clinic';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';
import type { Entry } from 'contentful';

type Props = {
  entry: LocationPageType;
  reviewStats: Record<string, number>;
  doctors: [];
  clinics: ClinicPage[];
  cta: Entry<IBlockGenericCtaFields>;
  beforeAfterPhotos: APIBeforeAfterPhoto[];
};

const LocationPage = ({
  entry,
  doctors,
  clinics,
  cta,
  beforeAfterPhotos,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const recentStoriesItems = entry.recentStoriesItems?.items || [];

  const primaryContentLeftColumnItems =
    entry.primaryContentLeftColumn?.items || [];

  const primaryContentRightColumnItems =
    entry.primaryContentRightColumn?.items || [];

  const secondaryContentLeftColumnItems =
    entry.secondaryContentLeftColumn?.items || [];

  const secondaryContentRightColumnItems =
    entry.secondaryContentRightColumn?.items || [];

  return (
    <>
      <Hero entry={entry} />
      <div className="max-w-screen-xl mx-auto px-5 lg:px-4 my-10 lg:my-36">
        <h2 className="mb-10 lg:mb-6 hyphenate">
          {entry.primaryContentHeadline}
        </h2>

        <div className="lg:grid grid-cols-2 -mx-4">
          <div
            className={clsx(
              !primaryContentRightColumnItems.length && 'col-span-2',
            )}
          >
            {!!primaryContentLeftColumnItems.length && (
              <BlockLoaderNonFields
                entry={entry}
                field="primaryContentLeftColumn"
              />
            )}
          </div>

          <div className="lg:ml-28">
            {(entry.isItWorthIt ||
              entry.whatIsTheCost ||
              entry.isAVisaNeeded ||
              entry.isAVisaNeededRich) && (
              <div className="max-w-screen-xl mx-auto px-5 md:px-4">
                <div
                  className="rounded-lg border border-silver-light mb-8 lg:mx-3"
                  style={{ boxShadow: '0 2px 10px rgba(52, 50, 1, 0.12)' }}
                >
                  <div>
                    {entry.isItWorthIt && (
                      <div className="p-4 flex border-b border-silver-light items-center">
                        <div className="flex mr-4">
                          <Icon type="Thumb" />
                        </div>
                        <div>
                          <p className="text-navy font-bold mb-1">
                            {t('locationPage.isItWorthIt')}
                          </p>
                          <p className="text-charcoal leading-tight">
                            {entry.isItWorthIt}
                          </p>
                        </div>
                      </div>
                    )}
                    {entry.whatIsTheCost && (
                      <div className="p-4 border-b border-silver-light flex items-center">
                        <div className="flex mr-4">
                          <svg
                            width="43"
                            height="44"
                            viewBox="0 0 43 44"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g>
                              <circle
                                cx="21.5"
                                cy="22"
                                r="21.5"
                                fill="#FFCA58"
                              />
                              <path
                                d="M21.78 32.84C24.80 32.84 28.42 31.45 29.03 26.52L25.38 25.88C24.94 28.00 24.22 29.22 21.99 29.22C20.01 29.22 18.91 28.12 18.42 25.68H22.89L23.32 23.13H18.13V21.33H23.55L23.99 18.78H18.36C18.83 16.11 19.99 14.69 22.19 14.69C24.28 14.69 25.09 15.68 25.29 18.40L29.12 18C28.89 12.86 26.05 11.09 22.22 11.09C17.72 11.09 14.88 13.85 14.01 18.78H12.39L11.95 21.33H13.75V23.13H11.72L11.29 25.68H13.98C14.77 30.47 17.29 32.84 21.78 32.84Z"
                                fill="#FFFCF7"
                              />
                            </g>
                          </svg>
                        </div>
                        <div>
                          <p className="text-navy font-bold mb-1">
                            {t('locationPage.whatIsTheCost')}
                          </p>
                          <p className="text-charcoal leading-tight">
                            {entry.whatIsTheCost}
                          </p>
                        </div>
                      </div>
                    )}
                    {(entry.isAVisaNeeded || entry.isAVisaNeededRich) && (
                      <div className="p-4 flex items-center">
                        <div className="flex mr-4">
                          <Icon type="Visa" />
                        </div>
                        <div>
                          <p className="text-navy font-bold mb-1">
                            {t('locationPage.isAVisaNeeded')}
                          </p>
                          {entry.isAVisaNeededRich ? (
                            <RichText fields={entry.isAVisaNeededRich} />
                          ) : (
                            <p className="text-charcoal leading-tight">
                              {entry.isAVisaNeeded}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="max-w-screen-xl lg:mx-3">
              {!!primaryContentRightColumnItems.length && (
                <BlockLoaderNonFields
                  entry={entry}
                  field="primaryContentRightColumn"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <TrustBanner />
      <div className="max-w-screen-xl mx-auto px-5 lg:px-4 my-6">
        <h2 className="text-2-188r">{entry.secondaryContentHeadline}</h2>
        <div className="lg:grid grid-cols-2 -mx-4">
          <div
            className={clsx(
              !secondaryContentRightColumnItems.length && 'col-span-2',
            )}
          >
            {!!secondaryContentLeftColumnItems.length && (
              <BlockLoaderNonFields
                entry={entry}
                field="secondaryContentLeftColumn"
              />
            )}
          </div>
          <div className="lg:ml-28">
            {!!secondaryContentRightColumnItems.length && (
              <BlockLoaderNonFields
                entry={entry}
                field="secondaryContentRightColumn"
              />
            )}
          </div>
        </div>
      </div>
      <BeforeAfter entry={entry} beforeAfterPhotos={beforeAfterPhotos} />
      {!!doctors?.length && (
        <Doctors
          headline={entry.doctorSectionTitle}
          description={entry.doctorSectionDescription}
          items={doctors}
        />
      )}
      {!!clinics.length && (
        <Clinics
          headline={entry.clinicSectionTitle}
          description={entry.clinicSectionDescription}
          items={clinics}
          cta={cta}
        />
      )}
      {recentStoriesItems.length && (
        <RecentStories
          entries={recentStoriesItems}
          headline={entry.recentStoriesHeadline}
        />
      )}
      <GetInTouch />
      <ConversionBar yPageOffset={entry.funnel ? 600 : 300} />
    </>
  );
};

export default LocationPage;
